import { Button, Checkbox, Divider, Group, Select, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { Paths, PathValue } from "@mctypes/global.types";
import * as R from "ramda";
import React, { useEffect } from "react";
import { useDebounce } from "usehooks-ts";
import { ZodError } from "zod";
import { IInternJuridiskUnderkonsulentRapport } from "../../../../mc-shared/reportSchemas/internJuridiskSchema";

const UnderkonsulentRapport: React.FC<{
  createdAt?: string;
  data: IInternJuridiskUnderkonsulentRapport;
  onUpdateEditData: (data: IInternJuridiskUnderkonsulentRapport) => void;
  projectId?: string;
}> = ({ createdAt, data, onUpdateEditData, projectId }) => {
  const [editData, setEditData] = React.useState(R.clone(data));

  useEffect(() => {
    onUpdateEditData(data);
  }, []);

  React.useEffect(() => {
    setEditData(null);
    setTimeout(() => {
      setEditData(R.clone(data));
    }, 50);
  }, [createdAt]);

  const updateData = <P extends Paths<IInternJuridiskUnderkonsulentRapport, 4>>(
    path: P,
    value: PathValue<IInternJuridiskUnderkonsulentRapport, P>,
  ) => {
    setEditData((prevState) => {
      const updatedData = R.assocPath(path.split("."), value, prevState);
      return updatedData;
    });
    onUpdateEditData(R.assocPath(path.split("."), value, editData));
  };

  const updateRow = (path: string, index: number, key: string, value: string) => {
    if (path === "nokkelpersoner") {
      let nokkelpersoner = R.clone(editData.underkonsulentensNokkelpersoner.nokkelpersoner);
      nokkelpersoner[index][key] = value;
      updateData("underkonsulentensNokkelpersoner.nokkelpersoner", nokkelpersoner);
    }
    if (path === "dagmulktEllerErstatning") {
      let dagmulktFrister = R.clone(editData.dagmulktEllerErstatning.dagmulktBelagteFrister);
      dagmulktFrister[index][key] = value;
      updateData("dagmulktEllerErstatning.dagmulktBelagteFrister", dagmulktFrister);
    }
  };

  const addNewRow = (path: string) => {
    if (path === "nokkelpersoner") {
      const newDeltager = {
        navn: "",
        fagdisiplin: "",
      };

      const updatedDeltagere = [...editData.underkonsulentensNokkelpersoner.nokkelpersoner, newDeltager];

      updateData("underkonsulentensNokkelpersoner.nokkelpersoner", updatedDeltagere);
    }

    if (path === "dagmulktEllerErstatning") {
      const newDagmulkt = {
        frist: "",
        sats: "2 ‰ av kontraktssum eller antatt totalt honorar eks. mva., minimum 1000 kr",
      };

      const updatedDagmulkter = [...editData.dagmulktEllerErstatning.dagmulktBelagteFrister, newDagmulkt];

      updateData("dagmulktEllerErstatning.dagmulktBelagteFrister", updatedDagmulkter);
    }
  };

  const removeRow = (path: string, index: number) => {
    if (path === "nokkelpersoner") {
      const updatedNokkelpersoner = [...editData.underkonsulentensNokkelpersoner.nokkelpersoner];

      updatedNokkelpersoner.splice(index, 1);
      updateData("underkonsulentensNokkelpersoner.nokkelpersoner", updatedNokkelpersoner);
    }
    if (path === "dagmulktEllerErstatning") {
      const updatedDagmulktFrister = [...editData.dagmulktEllerErstatning.dagmulktBelagteFrister];

      updatedDagmulktFrister.splice(index, 1);
      updateData("dagmulktEllerErstatning.dagmulktBelagteFrister", updatedDagmulktFrister);
    }
  };

  return (
    <Stack p="md" style={{ maxWidth: 1000, margin: "0 auto" }}>
      {editData != null && (
        <>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Generelt
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Stack w="50%">
            <Group>
              <Text w={200}>Prosjektnavn:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.prosjektNavn}
                onChange={(e) => updateData("generellInfo.prosjektNavn", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Kort beskrivelse av oppdrag:</Text>
              <Textarea
                flex={1}
                size="sm"
                minRows={2}
                autosize
                value={editData.generellInfo?.oppdragsBeskrivelse}
                onChange={(e) => updateData("generellInfo.oppdragsBeskrivelse", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Dato:</Text>
              <TextInput
                flex={1}
                size="sm"
                type="date"
                value={editData.generellInfo?.dato}
                onChange={(e) => updateData("generellInfo.dato", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Sted:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.sted}
                onChange={(e) => updateData("generellInfo.sted", e.currentTarget.value)}
              />
            </Group>
          </Stack>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Multiconsults representant
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Stack w="50%">
            <Group>
              <Text w={200}>Navn:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.multiRepresentant.navn}
                onChange={(e) => updateData("generellInfo.multiRepresentant.navn", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Tittel:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.multiRepresentant.tittel}
                onChange={(e) => updateData("generellInfo.multiRepresentant.tittel", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Email:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.multiRepresentant.kontaktDetaljer.email}
                onChange={(e) =>
                  updateData("generellInfo.multiRepresentant.kontaktDetaljer.email", e.currentTarget.value)
                }
              />
            </Group>
            <Group>
              <Text w={200}>Tlf:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.multiRepresentant.kontaktDetaljer.tlf}
                onChange={(e) =>
                  updateData("generellInfo.multiRepresentant.kontaktDetaljer.tlf", e.currentTarget.value)
                }
              />
            </Group>
          </Stack>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Underkonsulent
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Stack w="50%">
            <Group>
              <Text w={200}>Navn:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.navn}
                onChange={(e) => updateData("generellInfo.underkonsulent.navn", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Organisasjonsnummer:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.orgNummer}
                onChange={(e) => updateData("generellInfo.underkonsulent.orgNummer", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Adresse:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.adresse}
                onChange={(e) => updateData("generellInfo.underkonsulent.adresse", e.currentTarget.value)}
              />
            </Group>
          </Stack>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Underkonsulentens representant
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Stack w="50%">
            <Group>
              <Text w={200}>Navn:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.representant.navn}
                onChange={(e) => updateData("generellInfo.underkonsulent.representant.navn", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Tittel:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.representant.tittel}
                onChange={(e) => updateData("generellInfo.underkonsulent.representant.tittel", e.currentTarget.value)}
              />
            </Group>
            <Group>
              <Text w={200}>Email:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.representant.kontaktDetaljer.email}
                onChange={(e) =>
                  updateData("generellInfo.underkonsulent.representant.kontaktDetaljer.email", e.currentTarget.value)
                }
              />
            </Group>
            <Group>
              <Text w={200}>Tlf:</Text>
              <TextInput
                flex={1}
                size="sm"
                value={editData.generellInfo?.underkonsulent.representant.kontaktDetaljer.tlf}
                onChange={(e) =>
                  updateData("generellInfo.underkonsulent.representant.kontaktDetaljer.tlf", e.currentTarget.value)
                }
              />
            </Group>
          </Stack>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Avtaledokumenter
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Stack>
            <Group align="center">
              <Text>(c) Underkonsulentens endelige tilbud av {editData?.generellInfo.underkonsulent.navn}:</Text>
              <Checkbox
                checked={editData?.avtaleDokumenter?.avtaleC}
                onChange={() => updateData("avtaleDokumenter.avtaleC", !editData?.avtaleDokumenter?.avtaleC)}
              />
            </Group>
            <Group align="center">
              <Text>(d) Konsulentens tilbudsgrunnlag av {editData?.generellInfo.underkonsulent.navn}:</Text>
              <Checkbox
                checked={editData?.avtaleDokumenter?.avtaleD}
                onChange={() => updateData("avtaleDokumenter.avtaleD", !editData?.avtaleDokumenter?.avtaleD)}
              />
            </Group>
            <Group align="center">
              <Text>(e) NS-standard:</Text>
              <Select
                value={editData.avtaleDokumenter?.avtaleE || ""}
                onChange={(value) => updateData("avtaleDokumenter.avtaleE", value)}
                data={["NS 8401:2010", "NS 8402:2010", "NS 8404:2013"]}
                size="sm"
              />
            </Group>
          </Stack>
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Underkonsulentens nøkkelpersoner
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Group align="center">
            <Text>Skal nøkkelpersoner angis?</Text>
            <Checkbox
              checked={editData?.underkonsulentensNokkelpersoner?.isActive}
              onChange={() =>
                updateData(
                  "underkonsulentensNokkelpersoner.isActive",
                  !editData?.underkonsulentensNokkelpersoner?.isActive,
                )
              }
            />
          </Group>
          {editData?.underkonsulentensNokkelpersoner.isActive && (
            <Stack>
              <Button maw={"fit-content"} onClick={() => addNewRow("nokkelpersoner")}>
                Legg til deltager
              </Button>
              {editData?.underkonsulentensNokkelpersoner.nokkelpersoner.map((person, index) => (
                <Group key={index} align="center">
                  <TextInput
                    placeholder="Navn"
                    flex={1}
                    size="sm"
                    value={person?.navn}
                    onChange={(e) => updateRow("nokkelpersoner", index, "navn", e.currentTarget.value)}
                  />
                  <TextInput
                    placeholder="Fagdisiplin"
                    flex={1}
                    size="sm"
                    value={person?.fagdisiplin}
                    onChange={(e) => updateRow("nokkelpersoner", index, "fagdisiplin", e.currentTarget.value)}
                  />
                  <Button color="red" variant="filled" onClick={() => removeRow("nokkelpersoner", index)}>
                    Slett
                  </Button>
                </Group>
              ))}
            </Stack>
          )}
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Dagmulkt eller erstatning ved forsinkelse hos underkonsulenten
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Group align="center">
            <Text>Dagmulkt skal ikke gjelde for underkonsulentoppdraget:</Text>
            <Checkbox
              checked={editData?.dagmulktEllerErstatning?.altEn}
              onChange={() =>
                updateData("dagmulktEllerErstatning", {
                  ...editData?.dagmulktEllerErstatning,
                  altEn: !editData?.dagmulktEllerErstatning?.altEn,
                  altTo: false,
                })
              }
            />
          </Group>
          <Group align="center">
            <Text>Dagmulkt skal gjelde for underkonsulentoppdraget med følgende frister:</Text>
            <Checkbox
              checked={editData?.dagmulktEllerErstatning?.altTo}
              onChange={() =>
                updateData("dagmulktEllerErstatning", {
                  ...editData?.dagmulktEllerErstatning,
                  altTo: !editData?.dagmulktEllerErstatning?.altTo,
                  altEn: false,
                })
              }
            />
          </Group>
          {editData?.dagmulktEllerErstatning.altTo && (
            <Stack>
              <Button maw={"fit-content"} onClick={() => addNewRow("dagmulktEllerErstatning")}>
                Legg til frist
              </Button>
              {editData?.dagmulktEllerErstatning.dagmulktBelagteFrister.map((frist, index) => (
                <Group key={index} align="center">
                  <TextInput
                    flex={1}
                    size="sm"
                    type="date"
                    value={frist?.frist}
                    onChange={(e) => updateRow("dagmulktEllerErstatning", index, "frist", e.currentTarget.value)}
                  />
                  <TextInput
                    flex={1}
                    size="sm"
                    value={frist?.sats}
                    onChange={(e) => updateRow("dagmulktEllerErstatning", index, "sats", e.currentTarget.value)}
                  />
                  <Button color="red" variant="filled" onClick={() => removeRow("dagmulktEllerErstatning", index)}>
                    Slett
                  </Button>
                </Group>
              ))}
              <Text size="sm">Dersom det er avtalt andre satser kan dette fylles inn i tabellen over.</Text>
            </Stack>
          )}
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Ansvarsbegrensninger
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Text size="md" weight={600}>
            Ansvarsbegrensning for forsinkelse:
          </Text>
          <Group>
            <Text>Standardvilkårenes ansvarsbegrensning for forsinkelse gjelder.</Text>
            <Checkbox
              checked={editData?.ansvarsbegrensninger?.forsinkelse.altEn}
              onChange={() =>
                updateData("ansvarsbegrensninger.forsinkelse", {
                  ...editData?.ansvarsbegrensninger.forsinkelse,
                  altEn: !editData?.ansvarsbegrensninger.forsinkelse.altEn,
                  altTo: false,
                })
              }
            />
          </Group>
          <Group>
            <Text>
              I stedet for den ansvarsbegrensning som følger av Standardvilkårene, skal følgende bestemmelser gjelde for
              Underkonsulentoppdraget:
            </Text>
            <Checkbox
              checked={editData?.ansvarsbegrensninger.forsinkelse?.altTo}
              onChange={() =>
                updateData("ansvarsbegrensninger.forsinkelse", {
                  ...editData?.ansvarsbegrensninger.forsinkelse,
                  altTo: !editData?.ansvarsbegrensninger.forsinkelse?.altTo,
                  altEn: false,
                })
              }
            />
          </Group>
          {editData?.ansvarsbegrensninger.forsinkelse.altTo && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.ansvarsbegrensninger?.forsinkelse.bestemmelser}
              onChange={(e) => updateData("ansvarsbegrensninger.forsinkelse.bestemmelser", e.currentTarget.value)}
            />
          )}
          <Text size="md" weight={600} mt="sm">
            Ansvarsbegrensning for rådgivningsfeil/prosjekteringsfeil:
          </Text>
          <Group>
            <Text>Standardvilkårenes ansvarsbegrensning for prosjekteringsfeil gjelder.</Text>
            <Checkbox
              checked={editData?.ansvarsbegrensninger?.prosjekteringsfeil.altEn}
              onChange={() =>
                updateData("ansvarsbegrensninger.prosjekteringsfeil", {
                  ...editData?.ansvarsbegrensninger.prosjekteringsfeil,
                  altEn: !editData?.ansvarsbegrensninger?.prosjekteringsfeil.altEn,
                  altTo: false,
                })
              }
            />
          </Group>
          <Group>
            <Text>
              I stedet for de ansvarsbegrensninger som følger Standardvilkårene, skal følgende bestemmelser gjelde for
              Underkonsulentoppdraget:
            </Text>
            <Checkbox
              checked={editData?.ansvarsbegrensninger.prosjekteringsfeil?.altTo}
              onChange={() =>
                updateData("ansvarsbegrensninger.prosjekteringsfeil", {
                  ...editData?.ansvarsbegrensninger.prosjekteringsfeil,
                  altTo: !editData?.ansvarsbegrensninger.prosjekteringsfeil?.altTo,
                  altEn: false,
                })
              }
            />
          </Group>
          {editData?.ansvarsbegrensninger.prosjekteringsfeil.altTo && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.ansvarsbegrensninger?.prosjekteringsfeil.bestemmelser}
              onChange={(e) =>
                updateData("ansvarsbegrensninger.prosjekteringsfeil.bestemmelser", e.currentTarget.value)
              }
            />
          )}
          <Divider
            my="sm"
            label={
              <Text fz="lg" fw={600}>
                Vedlegg
              </Text>
            }
            labelPosition="left"
            mb="xs"
          />
          <Group align="center">
            <Text>Vedlegg A - Underkonsulentens ytelser:</Text>
            <Checkbox
              checked={editData?.vedlegg?.vedleggA.isActive}
              onChange={() => updateData("vedlegg.vedleggA.isActive", !editData?.vedlegg?.vedleggA.isActive)}
            />
          </Group>
          {editData?.vedlegg?.vedleggA.isActive && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.vedlegg?.vedleggA.beskrivelse}
              onChange={(e) => updateData("vedlegg.vedleggA.beskrivelse", e.currentTarget.value)}
            />
          )}
          <Group align="center" mt="sm">
            <Text>Vedlegg B - Underkonsulentens fremdrift:</Text>
            <Checkbox
              checked={editData?.vedlegg?.vedleggB.isActive}
              onChange={() => updateData("vedlegg.vedleggB.isActive", !editData?.vedlegg?.vedleggB.isActive)}
            />
          </Group>
          {editData?.vedlegg?.vedleggB.isActive && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.vedlegg?.vedleggB.beskrivelse}
              onChange={(e) => updateData("vedlegg.vedleggB.beskrivelse", e.currentTarget.value)}
            />
          )}
          <Group align="center" mt="sm">
            <Text>Vedlegg C - Underkonsulentens vederlag og utgiftsdekning:</Text>
            <Checkbox
              checked={editData?.vedlegg?.vedleggC.isActive}
              onChange={() => updateData("vedlegg.vedleggC.isActive", !editData?.vedlegg?.vedleggC.isActive)}
            />
          </Group>
          {editData?.vedlegg?.vedleggC.isActive && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.vedlegg?.vedleggC.beskrivelse}
              onChange={(e) => updateData("vedlegg.vedleggC.beskrivelse", e.currentTarget.value)}
            />
          )}
          <Group align="center" mt="sm">
            <Text>Vedlegg D - Erklæring fra samarbeidspartner | Forretningsetikk og samfunnsansvar:</Text>
            <Text>
              <a
                href="https://livelink.multiconsult.no/ll/livelink.exe?func=ll&objaction=overview&objid=17785164"
                target="_blank"
                rel="noopener noreferrer"
              >
                Dokument ligger her
              </a>
            </Text>
          </Group>
          <Group align="center" mt="sm">
            <Text>Vedlegg E - Underkonsulentens vederlag og utgiftsdekning:</Text>
            <Checkbox
              checked={editData?.vedlegg?.vedleggE.isActive}
              onChange={() => updateData("vedlegg.vedleggE.isActive", !editData?.vedlegg?.vedleggE.isActive)}
            />
          </Group>
          {editData?.vedlegg?.vedleggE.isActive && (
            <Textarea
              autosize
              minRows={2}
              value={editData?.vedlegg?.vedleggE.beskrivelse}
              onChange={(e) => updateData("vedlegg.vedleggE.beskrivelse", e.currentTarget.value)}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default UnderkonsulentRapport;
